import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { OverlayService } from '@fc-shared/ui/fc-overlay/overlay.service';
import {
  QuickSearchType,
  QuickSearchEntityType,
  QuickSearchApiItem,
} from '@fc-search/models/quick-search';
import { QuickSearchStore } from '@fc-search/store/quick-search.store';
import { getCurrentEmployee } from '@fc-store/selectors';
import { SearchApiService } from './search-api.service';

const ENTITY_BUTTON_COUNT: Record<QuickSearchEntityType, number> = {
  vehicle_vehicle: 2,
  authentication_employee: 2,
  task_task: 2,
  task_workflow: 2,
  poi_location: 1,
  crm_contact: 1,
  crm_company: 1,
  chat_channel: 1,
};

@Injectable({
  providedIn: 'root',
})
export class QuickSearchFacadeService {
  private router = inject(Router);
  private ngrxStore = inject(Store);
  private store = inject(QuickSearchStore);
  private overlayService = inject(OverlayService);
  private searchApi = inject(SearchApiService);
  private currentEmployee = this.ngrxStore.selectSignal(getCurrentEmployee);

  // State signals
  query = this.store.query;
  selectedType = this.store.selectedType;
  results = this.store.results;
  total = this.store.total;
  loading = this.store.loading;
  readonly recentItems = computed(() => this.store.recentItems.value() ?? []);

  // Navigation state
  selectedIndex = signal(-1);
  selectedButtonIndex = signal(-1);

  // Emits (increments) whenever mobile search should close
  mobileCloseRequested = signal(0);

  // Emits (increments) when a quick-action button should be activated
  buttonActivated = signal(0);

  // Emits (increments) when the global search overlay should open/toggle
  openRequested = signal(0);

  // Whether the search overlay (desktop or mobile) is currently open
  isSearchOpen = signal(false);

  // Emits (increments) when the search input should be focused (typing redirect)
  focusInputRequested = signal(0);

  constructor() {
    effect(() => {
      if (this.isSearchOpen()) {
        this.store.recentItems.reload();
      }
    });
  }

  onGlobalKeyDown = (event: KeyboardEvent): void => {
    if (event.code === 'KeyK' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      this.openRequested.update((n) => n + 1);
      return;
    }

    if (!this.isSearchOpen()) return;
    if (this.isTextInput(event.target)) return;
    if (this.tryRestoreInputFocus(event)) return;
    this.handleKeyDown(event);
  };

  private isTextInput(target: EventTarget | null): boolean {
    const element = target as HTMLElement | null;
    if (!element) return false;
    return !!element.closest(
      'input, textarea, select, button, [contenteditable]:not([contenteditable="false"])',
    );
  }

  private tryRestoreInputFocus(event: KeyboardEvent): boolean {
    if (event.defaultPrevented) return false;
    if (event.ctrlKey || event.metaKey || event.altKey) return false;

    if (event.key === 'Backspace') {
      event.preventDefault();
      this.focusInputRequested.update((n) => n + 1);
      this.setQuery(this.query().slice(0, -1));
      return true;
    }

    if (event.key.length !== 1) return false;

    event.preventDefault();
    this.focusInputRequested.update((n) => n + 1);
    this.setQuery(`${this.query()}${event.key}`);
    return true;
  }

  handleKeyDown(event: KeyboardEvent): void {
    const items =
      this.results().length > 0 ? this.results() : this.recentItems();

    switch (event.key) {
      case 'ArrowDown':
        if (items.length === 0) return;
        event.preventDefault();
        this.selectedButtonIndex.set(-1);
        {
          const hasViewAll =
            this.results().length > 0 && items === this.results();
          this.navigateDown(items.length + (hasViewAll ? 1 : 0));
        }
        break;
      case 'ArrowUp':
        if (items.length === 0) return;
        event.preventDefault();
        this.selectedButtonIndex.set(-1);
        this.navigateUp();
        break;
      case 'ArrowRight':
        if (this.selectedIndex() < 0 || this.selectedIndex() >= items.length)
          return;
        event.preventDefault();
        this.navigateButtonRight(items);
        break;
      case 'ArrowLeft':
        if (this.selectedIndex() < 0 || this.selectedIndex() >= items.length)
          return;
        event.preventDefault();
        this.navigateButtonLeft();
        break;
      case 'Enter':
        event.preventDefault();
        if (this.selectedButtonIndex() >= 0) {
          this.buttonActivated.update((v) => v + 1);
        } else if (this.selectedIndex() >= 0) {
          this.selectCurrentItem(items);
        } else if (!this.query().trim()) {
          this.router.navigate(['/search']);
          this.clearAndClose();
        } else if (!this.loading() && this.results().length === 0) {
          this.navigateToViewAll();
        } else {
          this.selectCurrentItem(items);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.clearAndClose();
        break;
    }
  }

  private navigateDown(maxLength: number): void {
    const current = this.selectedIndex();
    if (current < maxLength - 1) {
      this.selectedIndex.set(current + 1);
    }
  }

  private navigateUp(): void {
    const current = this.selectedIndex();
    if (current > 0) {
      this.selectedIndex.set(current - 1);
    } else if (current === 0) {
      this.selectedIndex.set(-1); // Reset to no selection
    }
  }

  private navigateButtonRight(items: QuickSearchApiItem[]): void {
    const idx = this.selectedIndex();
    const item = items[idx];
    if (!item) return;
    const maxButtons = this.getButtonCount(item);
    if (maxButtons === 0) return;
    const current = this.selectedButtonIndex();
    if (current < maxButtons - 1) {
      this.selectedButtonIndex.set(current + 1);
    }
  }

  private getButtonCount(item: QuickSearchApiItem): number {
    if (item.entityType !== 'authentication_employee') {
      return ENTITY_BUTTON_COUNT[item.entityType] ?? 0;
    }

    const currentEmployeeId = this.currentEmployee()?.id;
    return currentEmployeeId === item.obj.id ? 1 : 2;
  }

  private navigateButtonLeft(): void {
    const current = this.selectedButtonIndex();
    if (current > -1) {
      this.selectedButtonIndex.set(current - 1);
    }
  }

  selectCurrentItem(items?: QuickSearchApiItem[]): void {
    const index = this.selectedIndex();
    const list =
      items ??
      (this.results().length > 0 ? this.results() : this.recentItems());

    if (index >= 0 && index < list.length) {
      this.navigateToItem(list[index]);
    } else if (
      index === list.length &&
      this.results().length > 0 &&
      list === this.results()
    ) {
      this.navigateToViewAll();
    }
  }

  navigateToItem(item: QuickSearchApiItem): void {
    const route = this.getRouteForItem(item);
    if (route) {
      this.trackItemOpened(item);
      this.router.navigate(route.commands, { queryParams: route.queryParams });
      this.clearAndClose();
    }
  }

  trackItemOpened(item: QuickSearchApiItem): void {
    this.searchApi.quickSearchEntityOpened(
      item.entityType,
      String(item.obj.id),
    );
  }

  private getRouteForItem(
    item: QuickSearchApiItem,
  ): { commands: string[]; queryParams?: Record<string, unknown> } | null {
    switch (item.entityType) {
      case 'vehicle_vehicle':
        return {
          commands: ['/vehicles/main/vehicles', String(item.obj.id), 'details'],
        };
      case 'authentication_employee':
        return { commands: ['/users/main/users', item.obj.id, 'overview'] };
      case 'poi_location':
        return { commands: ['/locations/main/location-list', item.obj.id] };
      case 'task_task':
        return {
          commands: ['/tasks/kanban', item.obj.workflow.id],
          queryParams: { dialog: 'task', taskId: item.obj.id },
        };
      case 'task_workflow':
        return { commands: ['/tasks/kanban', item.obj.id] };
      case 'crm_contact':
        return {
          commands: ['/crm/main/contacts', String(item.obj.id), 'overview'],
        };
      case 'crm_company':
        return { commands: ['/crm/main/companies', String(item.obj.id)] };
      case 'chat_channel':
        return { commands: ['/chats/direct-message', item.obj.id] };
    }
  }

  private navigateToViewAll(): void {
    const type = this.selectedType();
    const typeMap: Record<QuickSearchType, string> = {
      all: '',
      vehicle_vehicle: 'vehicles',
      authentication_employee: 'employees',
      poi_location: 'locations',
      task: 'tasks',
      chat_channel: 'chats',
      crm: 'crm',
    };
    const entity = typeMap[type];
    const queryParams = entity
      ? { query: this.query(), entity }
      : { query: this.query() };
    this.router.navigate(['/search'], { queryParams });
    this.clearAndClose();
  }

  // State mutations
  setQuery(value: string): void {
    this.store.setQuery(value);
    this.selectedIndex.set(-1);
    this.selectedButtonIndex.set(-1);
    if (!value.trim()) {
      this.store.clearResults();
      return;
    }
    this.store.search();
  }

  setType(type: QuickSearchType): void {
    this.store.setType(type);
    this.selectedIndex.set(-1);
    this.selectedButtonIndex.set(-1);
    if (this.store.query().trim()) {
      this.store.search();
    }
  }

  clearSearch(): void {
    this.store.clearSearch();
    this.selectedIndex.set(-1);
    this.selectedButtonIndex.set(-1);
  }

  clearAndClose(): void {
    this.clearSearch();
    this.overlayService.closeAll();
    this.mobileCloseRequested.update((v) => v + 1);
  }

  // Reset selection when overlay opens
  resetSelection(): void {
    this.selectedIndex.set(-1);
    this.selectedButtonIndex.set(-1);
  }
}
